// src/utils/imageCompression.js

/**
 * Image compression utility
 * Combines mobile-optimized compression with validation helpers
 */

/**
 * Compress image to target size while maintaining quality
 * @param {File} file - Image file to compress
 * @param {number} maxSizeKB - Target size in KB (default: 700)
 * @param {boolean} isMobile - Is mobile device (default: auto-detect)
 * @returns {Promise<string>} Base64 encoded compressed image
 */
export const compressImage = (file, maxSizeKB = 700, isMobile = null) => {
  // Auto-detect mobile if not specified
  const isMobileDevice = isMobile !== null ? isMobile : checkIsMobile();

  // Mobile devices get smaller target size
  const targetSizeKB = isMobileDevice ? Math.min(maxSizeKB, 150) : maxSizeKB;

  return new Promise((resolve, reject) => {
    // Validate file type
    if (!file || !file.type.startsWith("image/")) {
      reject(new Error("Invalid file type. Please select an image."));
      return;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    // ✅ Store original file size for logging
    const originalSize = file.size;

    img.onload = () => {
      // Set max dimensions based on device
      const maxDimension = isMobileDevice ? 800 : 1200;
      let { width, height } = img;

      // Calculate new dimensions to maintain aspect ratio
      if (width > height) {
        if (width > maxDimension) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        }
      } else {
        if (height > maxDimension) {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw image on canvas
      ctx.drawImage(img, 0, 0, width, height);

      // Start with high quality and reduce if needed
      let quality = isMobileDevice ? 0.7 : 0.9;
      let compressedDataUrl;

      do {
        compressedDataUrl = canvas.toDataURL("image/jpeg", quality);
        quality -= 0.1;
      } while (
        compressedDataUrl.length > targetSizeKB * 1024 * 1.37 &&
        quality > 0.1
      );
      // 1.37 accounts for base64 encoding overhead

      // Calculate final compressed size
      const compressedSize = getBase64Size(compressedDataUrl);
      const compressionRatio = Math.round(
        ((originalSize - compressedSize) / originalSize) * 100,
      );

      // ✅ SIMPLE ONE-LINE LOG
      console.log(
        `🖼️ "${file.name}": ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (${compressionRatio}% compression)`,
      );

      // Clean up
      URL.revokeObjectURL(img.src);
      resolve(compressedDataUrl);
    };

    img.onerror = () => {
      reject(new Error("Failed to load image. Please try again."));
    };

    // Load image from file
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Get file size from base64 string
 * @param {string} base64String - Base64 encoded string
 * @returns {number} File size in bytes
 */
export const getBase64Size = (base64String) => {
  if (!base64String) return 0;
  const base64Data = base64String.split(",")[1] || base64String;
  return Math.round((base64Data.length * 3) / 4);
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Check if device is mobile
 * @returns {boolean} True if mobile device
 */
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
};

/**
 * Internal helper - check if mobile (alias for isMobileDevice)
 */
const checkIsMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
};

/**
 * Validate and compress image file
 * @param {File} file - Image file
 * @param {number} maxSizeKB - Maximum size in KB
 * @returns {Promise<Object>} Result with compressed image and stats
 */
export const validateAndCompressImage = async (file, maxSizeKB = 700) => {
  try {
    // Validate file type
    if (!file.type.startsWith("image/")) {
      throw new Error(`File "${file.name}" is not a valid image format.`);
    }

    const originalSize = file.size;
    const needsCompression = originalSize > 1024 * 1024; // 1MB

    // If file is small enough, just convert to base64
    if (!needsCompression) {
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });

      const compressedSize = getBase64Size(base64);

      // ✅ SIMPLE ONE-LINE LOG FOR SMALL FILES
      console.log(
        `🖼️ "${file.name}": ${formatFileSize(originalSize)} (no compression needed)`,
      );

      return {
        compressed: base64,
        originalSize,
        compressedSize: compressedSize,
        wasCompressed: false,
      };
    }

    // Compress the image
    const compressed = await compressImage(file, maxSizeKB);
    const compressedSize = getBase64Size(compressed);

    return {
      compressed,
      originalSize,
      compressedSize,
      wasCompressed: true,
      compressionRatio: Math.round(
        ((originalSize - compressedSize) / originalSize) * 100,
      ),
    };
  } catch (error) {
    console.error("Error compressing image:", error);
    throw error;
  }
};
// 8
